// Phase 13 (DISP-01, DISP-02, DISP-08) — GraphQL-Operations fuer Admin-Payment-Link-Dispatch.
//
// Diese Operations werden von Plan 13-03 Backend-seitig implementiert. Der Client-seitige
// Codegen (src/client/generated/graphql.tsx) laeuft post-merge; bis dahin nutzen die
// Komponenten `useMutation(DOC)` / `useQuery(DOC)` direkt mit den hier exportierten
// GraphQL-Documents.
//
// Die Namespace-Wahl "payments/mollie.graphql.ts" spiegelt den Row-Action-Kontext aus
// OpenPaymentsList wider. Die bestehenden Mollie-Setup-Operations liegen in
// `src/client/containers/settings/mollie.graphql.ts` — beide Files sind
// komplementaer (Setup vs. Dispatch).

import {gql} from "@apollo/client";

/**
 * DISP-01 — Einzel-Dispatch eines Mollie-Zahlungslinks fuer eine offene ScheduledPayment.
 * Rate-Limit: 5/h pro Buchung (shared mit Bundle-Mutation).
 */
export const SEND_MOLLIE_PAYMENT_LINK_FROM_ADMIN = gql`
    mutation SendMolliePaymentLinkFromAdmin($scheduledPaymentId: ID!, $locale: String) {
        sendMolliePaymentLinkFromAdmin(scheduledPaymentId: $scheduledPaymentId, locale: $locale) {
            molliePaymentId
            checkoutUrl
            status
            linkExpiresAt
            bookerEmail
            dispatchKind
        }
    }
`;

/**
 * DISP-02 — Bundle-Dispatch aller offenen Mollie-SPs einer Buchung in einer
 * einzigen Mollie-Payment mit `metadata.scheduledPaymentIds`.
 * Rate-Limit: shared-Counter mit Einzel-Dispatch.
 */
export const SEND_MOLLIE_PAYMENT_LINK_FOR_BOOKING_FROM_ADMIN = gql`
    mutation SendMolliePaymentLinkForBookingFromAdmin($bookingId: ID!, $locale: String) {
        sendMolliePaymentLinkForBookingFromAdmin(bookingId: $bookingId, locale: $locale) {
            molliePaymentId
            checkoutUrl
            status
            linkExpiresAt
            bookerEmail
            dispatchKind
        }
    }
`;

/**
 * DISP-08 — Detektiert SPs die bereits anderweitig bezahlt sind (paymentDone != null),
 * aber in einem aktiven Bundle-Link enthalten sind. Liefert Liste der SP-IDs.
 * Gebaut als Query (idempotent, cache-baseable). Client rendert fuer jedes Ergebnis
 * einen orange Warn-Badge ("In aktivem Buendel enthalten").
 */
export const DETECT_BUNDLE_CONFLICTS_FROM_ADMIN = gql`
    query DetectBundleConflictsFromAdmin($bookingId: ID!) {
        detectBundleConflictsFromAdmin(bookingId: $bookingId)
    }
`;

// ════════════════════════════════════════════════════════════════════════════════
// Phase 23 / Plan 23-03 / BDSEC-04 — Admin Action Mutations
// ════════════════════════════════════════════════════════════════════════════════
//
// Vier neue Mutations fuer die MolliePaymentSection-UI in BookingDetails
// (Plan 23-05/06). Jede ist tenant-scoped (@Ctx koOrganizationId),
// feature-flag-gated, tombstone-safe, und schreibt einen PaymentActionLog-
// Eintrag (audit-trail invariant BDSEC-04). Service-Layer in mollie.payment.ts;
// Resolver in mollie.resolver.ts.

/**
 * BDSEC-04 — Storniert einen offenen/authorized MolliePaymentAttempt.
 * Idempotent: bereits canceled → no-op. T-23-02 (Mollie 422 race-safe).
 */
export const CANCEL_MOLLIE_PAYMENT_FROM_ADMIN = gql`
    mutation CancelMolliePaymentFromAdmin($scheduledPaymentId: ID!) {
        cancelMolliePaymentFromAdmin(scheduledPaymentId: $scheduledPaymentId) {
            molliePaymentId
            checkoutUrl
            status
        }
    }
`;

/**
 * BDSEC-04 — Pollt Mollie-Status synchron und wendet Forward-State-Update
 * an (T-23-03 — kein Rollback). Rate-Limit 60/min/tenant (ASVS V13).
 */
export const REFRESH_MOLLIE_PAYMENT_STATUS_FROM_ADMIN = gql`
    mutation RefreshMolliePaymentStatusFromAdmin($scheduledPaymentId: ID!) {
        refreshMolliePaymentStatusFromAdmin(scheduledPaymentId: $scheduledPaymentId) {
            id
            status
            paidAt
            molliePaymentId
        }
    }
`;

/**
 * BDSEC-04 — Klarna-Style Two-Step-Capture (authorized → paid).
 * Server-side amount validation (T-23-04); Pitfall 10 Klarna-only-guard.
 */
export const CAPTURE_MOLLIE_PAYMENT_FROM_ADMIN = gql`
    mutation CaptureMolliePaymentFromAdmin($scheduledPaymentId: ID!, $amount: Float) {
        captureMolliePaymentFromAdmin(scheduledPaymentId: $scheduledPaymentId, amount: $amount) {
            molliePaymentId
            checkoutUrl
            status
        }
    }
`;

/**
 * BDSEC-04 — Neuer Payment-Attempt (Retry nach failed/expired/canceled
 * oder Empty-State). Transaktional via existing createPayment + Partial-
 * Unique-Index UQ_mollie_attempt_current_per_sp (T-23-05 race-safe).
 */
export const CREATE_NEW_MOLLIE_PAYMENT_ATTEMPT_FROM_ADMIN = gql`
    mutation CreateNewMolliePaymentAttemptFromAdmin($scheduledPaymentId: ID!, $locale: String) {
        createNewMolliePaymentAttemptFromAdmin(
            scheduledPaymentId: $scheduledPaymentId,
            locale: $locale
        ) {
            molliePaymentId
            checkoutUrl
            status
            linkExpiresAt
        }
    }
`;

/**
 * Erfasst eine OFFENE Mollie-Rate als ausserhalb von Mollie bezahlt (z. B. Kunde
 * hat bei defektem Zahlungslink direkt per Ueberweisung/PayPal gezahlt). Setzt die
 * Rate auf bezahlt (voller offener Betrag), schreibt einen Payment-Record, flippt
 * den Provider Mollie -> Manual und entwertet den toten Link. Admin-only.
 */
export const RECORD_EXTERNAL_PAYMENT_FOR_SCHEDULED_PAYMENT = gql`
    mutation RecordExternalPaymentForScheduledPayment($input: RecordExternalPaymentForScheduledPaymentInput!) {
        recordExternalPaymentForScheduledPayment(input: $input) {
            id
            bookingId
            paymentProvider
            paymentDone
            paymentTransferType
            paymentAmount
            openAmount
            molliePaymentUrl
            molliePaymentStatus
            methodChangedAt
            note
        }
    }
`;
